/**
 * PROFILE MANAGER SERVICE
 * 
 * Gestiona los múltiples perfiles de un usuario:
 * - Crear/actualizar/eliminar perfiles
 * - Cambiar entre perfiles activos
 * - Gestionar permisos por perfil
 */

import { db } from '@/core/config/firebase';
import { Logger } from '@/core/utils';
import { 
  UserProfile, 
  ProfileType, 
  PatientProfileData,
  TherapistProfileData,
  SchoolAdminProfileData,
  ProfileData
} from '../../types/profiles';

const logger = Logger.getLogger('ProfileManagerService');

class ProfileManagerService {
  /**
   * Normaliza la estructura de un documento de perfil para soportar
   * tanto el esquema nuevo (data anidada) como el legado (campos planos).
   */
  private normalizeProfile(doc: any, fallbackUserId?: string): UserProfile {
    const raw = doc.data() || {};
    const {
      data: nestedData,
      id: storedId,
      userId,
      type,
      isActive,
      createdAt,
      updatedAt,
      ...rest
    } = raw;

    const profileData = (nestedData ?? rest) as ProfileData;
    const derivedUserId = userId ?? (profileData as any)?.userId ?? fallbackUserId ?? '';
    const derivedType = type ?? (profileData as any)?.type ?? ProfileType.PATIENT;

    return {
      id: doc.id,
      userId: derivedUserId,
      type: derivedType,
      data: profileData,
      isActive: isActive !== false,
      createdAt: createdAt ?? new Date().toISOString(),
      updatedAt: updatedAt ?? createdAt ?? new Date().toISOString()
    };
  }

  /**
   * Obtener todos los perfiles de un usuario
   */
  async getUserProfiles(userId: string): Promise<UserProfile[]> {
    try {
      const snapshot = await db.collection('profiles')
        .where('userId', '==', userId)
        .get();

      const profiles = snapshot.docs
        .map(doc => this.normalizeProfile(doc, userId))
        .filter(profile => profile.isActive !== false && profile.userId === userId);

      logger.info('ProfileManager: perfiles cargados', { userId, count: profiles.length });

      return profiles;
    } catch (error) {
      logger.error('ProfileManager: Error al cargar perfiles', error, { userId });
      throw error;
    }
  }

  /**
   * Obtener perfil específico
   */
  async getProfile(profileId: string): Promise<UserProfile | null> {
    const doc = await db.collection('profiles').doc(profileId).get();

    if (!doc.exists) {
      return null;
    }

    return this.normalizeProfile(doc);
  }

  /**
   * Obtener perfiles específicos usando sus IDs (fallback cuando la query por userId falla)
   */
  async getProfilesByIds(profileIds: string[], userId?: string): Promise<UserProfile[]> {
    if (!profileIds || profileIds.length === 0) {
      return [];
    }

    const docs = await Promise.all(
      profileIds.map(async profileId => {
        try {
          const snapshot = await db.collection('profiles').doc(profileId).get();
          return snapshot.exists ? this.normalizeProfile(snapshot, userId) : null;
        } catch (error) {
          logger.error('ProfileManager: Error obteniendo perfil por ID', error as Error, { profileId });
          return null;
        }
      })
    );

    const profiles = docs
      .filter((profile): profile is UserProfile => Boolean(profile))
      .filter(profile => profile.isActive !== false && (!userId || profile.userId === userId));

    logger.info('ProfileManager: fallback perfiles por ID', { userId, count: profiles.length });
    return profiles;
  }

  /**
   * Crear nuevo perfil
   */
  async createProfile(
    userId: string,
    type: ProfileType,
    data: any
  ): Promise<UserProfile> {
    // Usar colección unificada 'profiles'
    const profileRef = db.collection('profiles').doc();

    const profile: UserProfile = {
      id: profileRef.id,
      userId,
      type,
      data,
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    await profileRef.set(profile);

    logger.info('ProfileManager: Perfil creado', { profileId: profileRef.id, type, userId });

    return profile;
  }

  /**
   * Actualizar perfil
   */
  async updateProfile(
    profileId: string,
    type: ProfileType,
    updates: Partial<any>
  ): Promise<void> {
    // Usar colección unificada 'profiles'
    await db.collection('profiles').doc(profileId).set({
      data: updates,
      updatedAt: new Date().toISOString()
    }, { merge: true });
    logger.info('ProfileManager: Perfil actualizado', { profileId, type });
  }

  /**
   * Cambiar perfil activo del usuario
   */
  async switchProfile(userId: string, profileId: string): Promise<void> {
    await db.collection('users').doc(userId).update({
      currentProfileId: profileId
    });
  }

  /**
   * Desactivar perfil (soft delete)
   */
  async deactivateProfile(profileId: string, type: ProfileType): Promise<void> {
    // Usar colección unificada 'profiles'
    await db.collection('profiles').doc(profileId).update({
      isActive: false,
      updatedAt: new Date().toISOString()
    });
    logger.info('ProfileManager: Perfil desactivado', { profileId });
  }

  /**
   * Verificar si usuario tiene un tipo de perfil específico
   */
  async hasProfileType(userId: string, type: ProfileType): Promise<boolean> {
    // Buscar en colección unificada 'profiles'
    const snapshot = await db.collection('profiles')
      .where('userId', '==', userId)
      .where('type', '==', type)
      .where('isActive', '==', true)
      .limit(1)
      .get();

    return !snapshot.empty;
  }

  /**
   * Obtener perfil de terapeuta (helper común)
   */
  async getTherapistProfile(userId: string): Promise<UserProfile | null> {
    // Buscar en colección unificada 'profiles'
    const snapshot = await db.collection('profiles')
      .where('userId', '==', userId)
      .where('type', '==', ProfileType.THERAPIST)
      .where('isActive', '==', true)
      .limit(1)
      .get();

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    } as UserProfile;
  }

  /**
   * Obtener perfil de admin de escuela (helper común)
   */
  async getSchoolAdminProfile(
    userId: string, 
    schoolId: string
  ): Promise<UserProfile | null> {
    // Buscar en colección unificada 'profiles'
    const snapshot = await db.collection('profiles')
      .where('userId', '==', userId)
      .where('type', '==', ProfileType.SCHOOL_ADMIN)
      .where('schoolId', '==', schoolId)
      .where('isActive', '==', true)
      .limit(1)
      .get();

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    } as UserProfile;
  }

  /**
   * Crear perfil multi-rol (ej: Profesor de Institut AMAR)
   * 
   * Crea tanto el perfil de terapeuta como el de admin de escuela
   * y los vincula entre sí
   */
  async createDualProfile(
    userId: string,
    therapistData: TherapistProfileData,
    schoolAdminData: Omit<SchoolAdminProfileData, 'therapistProfileId'>
  ): Promise<{ therapist: UserProfile; schoolAdmin: UserProfile }> {
    // 1. Crear perfil de terapeuta
    const therapistProfile = await this.createProfile(
      userId,
      ProfileType.THERAPIST,
      therapistData
    );

    // 2. Crear perfil de admin de escuela con link al perfil de terapeuta
    const schoolAdminProfile = await this.createProfile(
      userId,
      ProfileType.SCHOOL_ADMIN,
      {
        ...schoolAdminData,
        therapistProfileId: therapistProfile.id
      }
    );

    return {
      therapist: therapistProfile,
      schoolAdmin: schoolAdminProfile
    };
  }
}

export const profileManager = new ProfileManagerService();

